import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Upload,
} from "antd";
import moment from "moment";
import Notification from "../../../components/Notification";
import utils from "../../../utils";
import product from "../../../assets/json/product.json";

const apiAdmin = require("../api/apiAdmin");
const apiOrder = require("../api/apiOrder");
const attachment = require("../../apiGeneral/attachment");
const { getProductById, getProductOptionById } = require("../../../assets/js/getProduct");

const STATUS = [
  { value: 1, label: "Chờ in", color: "processing" },
  { value: 2, label: "Đang cầm", color: "warning" },
  { value: 3, label: "Đã huỷ", color: "error" },
  { value: 4, label: "Hoàn thành", color: "success" },
];

const KENO_LABEL = {
  1: "Lẻ",
  2: "Chẵn",
  3: "Nhỏ",
  4: "Lớn",
  5: "Hoà",
  6: "Chẵn-Lẻ",
  7: "Hoà LN",
  8: "Chẵn 11-12",
  9: "Lẻ 11-12",
};

const PRODUCT_OPTIONS = Object.keys(product).map((id) => ({
  value: Number(id),
  label: product[id],
}));

const EMPTY_FILTER = {
  productId: null,
  statusId: null,
  staffId: null,
  ticketId: "",
  orderId: "",
  customerPhone: "",
  datePrintFrom: null,
  datePrintTo: null,
};

function money(value) {
  const amount = Number(value || 0) * 1000;
  if (Number.isNaN(amount)) return "-";
  return amount.getMoneyFormat();
}

function formatDate(value) {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm:ss") : value;
}

function statusOf(statusId) {
  return STATUS.find((item) => item.value === Number(statusId));
}

function ticketProfit(element) {
  if (Array.isArray(element)) return Number(element[2]) || 0;
  if (element && typeof element === "object") return Number(element.money || element.profit) || 0;
  return 0;
}

function winLabel(ticket) {
  if (!ticket) return "-";
  if (!ticket.isChecked) return "Chưa dò";
  const winning = Array.isArray(ticket.winingNumber) ? ticket.winingNumber : [];
  return winning.some((element) => ticketProfit(element) > 0) ? "Trúng thưởng" : "Không trúng";
}

function firstTicket(row) {
  return row && Array.isArray(row.tickets) && row.tickets.length ? row.tickets[0] : null;
}

function shipmentOf(row) {
  return row && row.shipment && typeof row.shipment === "object" ? row.shipment : {};
}

function renderNumbers(ticket, rowNumber) {
  if ([13, 14, 15].includes(ticket.productId)) {
    return (
      <span>
        {rowNumber.number}
        {rowNumber.ticketCodes && rowNumber.ticketCodes.length ? (
          <div>{rowNumber.ticketCodes.join("; ")}</div>
        ) : null}
      </span>
    );
  }
  const numbers = Array.isArray(rowNumber.number) ? rowNumber.number : [rowNumber.number];
  return numbers.map((number, index) => (
    <span key={index} style={{ marginRight: 6 }}>
      {ticket.productOptionId === 82 ? KENO_LABEL[number] || number : number}
    </span>
  ));
}

function OrderList() {
  const [draft, setDraft] = useState(EMPTY_FILTER);
  const [query, setQuery] = useState({ ...EMPTY_FILTER, page: 1, size: 20, reload: 0 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sumAll, setSumAll] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const isAdmin = utils.getUserinfo() && utils.getUserinfo().role === "admin";

  useEffect(() => {
    apiAdmin.litStaff((err, result) => {
      if (!err && Array.isArray(result)) {
        setStaffList(result.filter((staff) => staff.role === "clerk" && staff.isActive));
      }
    });
  }, []);

  useEffect(() => {
    if (!query.datePrintFrom || !query.datePrintTo) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [err, response] = await apiOrder.listAllOrder({
        productId: query.productId,
        statusId: query.statusId,
        staffId: query.staffId,
        ticketId: query.ticketId,
        orderId: query.orderId,
        customerPhone: query.customerPhone,
        datePrintFrom: query.datePrintFrom,
        datePrintTo: query.datePrintTo,
        page: query.page,
        size: query.size,
      });
      if (cancelled) return;
      setLoading(false);
      if (err) {
        Notification("error", "Có lỗi xảy ra", typeof err === "string" ? err : "Không tải được danh sách đơn hàng");
        return;
      }
      setRows(Array.isArray(response && response.data) ? response.data : []);
      setTotal(response && response.total ? Number(response.total) : 0);
      setSumAll(Array.isArray(response && response.sumAll) ? response.sumAll : []);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const onSearch = () => {
    if (!draft.datePrintFrom || !draft.datePrintTo) {
      Notification("warning", "Thiếu thời gian bán", "Vui lòng chọn thời gian bán để lọc đơn hàng");
      return;
    }
    setQuery((current) => ({
      ...current,
      ...draft,
      page: 1,
      reload: Date.now(),
    }));
  };

  const onExport = async () => {
    if (!draft.datePrintFrom || !draft.datePrintTo) return;
    setExporting(true);
    const [err] = await apiOrder.exportAllOrder({
      productId: draft.productId,
      statusId: draft.statusId,
      staffId: draft.staffId,
      ticketId: draft.ticketId,
      orderId: draft.orderId,
      customerPhone: draft.customerPhone,
      datePrintFrom: draft.datePrintFrom,
      datePrintTo: draft.datePrintTo,
    });
    setExporting(false);
    if (err) {
      Notification("error", "Có lỗi xảy ra", typeof err === "string" ? err : "Không xuất được file");
    }
  };

  const patchTicketImage = (ticketId, side, pathUrl) => {
    const patchTickets = (tickets) =>
      Array.isArray(tickets)
        ? tickets.map((item) => (item.id === ticketId ? { ...item, [side]: pathUrl } : item))
        : tickets;
    setSelected((current) => (current ? { ...current, tickets: patchTickets(current.tickets) } : current));
    setRows((current) => current.map((row) => ({ ...row, tickets: patchTickets(row.tickets) })));
  };

  const uploadTicketImage = (ticketId, side, file) => {
    if (!isAdmin) return false;
    const key = ticketId + "-" + side;
    setUploadingKey(key);
    attachment.apiAttachment(file, "ticket", async (err, result) => {
      if (err) {
        setUploadingKey(null);
        const message =
          (err.data && err.data._error_message) ||
          (typeof err === "string" ? err : "Không tải được ảnh");
        Notification("error", "Có lỗi xảy ra", message);
        return;
      }
      const pathUrl = result && result.pathUrl;
      if (!pathUrl) {
        setUploadingKey(null);
        Notification("error", "Có lỗi xảy ra", "Không nhận được đường dẫn ảnh");
        return;
      }
      const [updateErr] = await apiOrder.updateTicketImage(ticketId, { [side]: pathUrl });
      setUploadingKey(null);
      if (updateErr) {
        Notification("error", "Có lỗi xảy ra", typeof updateErr === "string" ? updateErr : "Không cập nhật được ảnh vé");
        return;
      }
      patchTicketImage(ticketId, side, pathUrl);
      Notification("success", "Đã cập nhật ảnh vé", "");
    });
    return false;
  };

  const staffName = (staffId) => {
    const staff = staffList.find((item) => item.id == staffId);
    return staff ? staff.username : staffId || "-";
  };

  const columns = [
    { title: "Id đơn", dataIndex: "id", width: 90 },
    {
      title: "Id vé",
      width: 90,
      render: (_, row) => {
        const ticket = firstTicket(row);
        return ticket ? ticket.id : "-";
      },
    },
    {
      title: "Sản phẩm",
      width: 140,
      render: (_, row) => getProductById(row.productId) || "-",
    },
    {
      title: "Kiểu chơi",
      width: 140,
      render: (_, row) => {
        const ticket = firstTicket(row);
        return ticket ? getProductOptionById(ticket.productOptionId) || "-" : "-";
      },
    },
    {
      title: "Phí dịch vụ",
      width: 120,
      render: (_, row) => money(row.servicePrice),
    },
    {
      title: "Tiền vé",
      width: 120,
      render: (_, row) => money(Number(row.totalPrice || 0) - Number(row.servicePrice || 0)),
    },
    {
      title: "Tổng",
      width: 120,
      render: (_, row) => money(row.totalPrice),
    },
    {
      title: "Tên khách",
      width: 160,
      render: (_, row) => shipmentOf(row).fullName || "-",
    },
    {
      title: "Số điện thoại",
      width: 130,
      render: (_, row) => shipmentOf(row).phoneNumber || "-",
    },
    {
      title: "Nhân viên",
      width: 140,
      render: (_, row) => staffName(row.staffId),
    },
    {
      title: "Trạng thái",
      width: 120,
      render: (_, row) => {
        const ticket = firstTicket(row);
        const status = ticket ? statusOf(ticket.statusId) : null;
        return status ? <Tag color={status.color}>{status.label}</Tag> : "-";
      },
    },
    {
      title: "Thời gian mua",
      width: 170,
      render: (_, row) => formatDate(firstTicket(row) && firstTicket(row).createdDate),
    },
    {
      title: "Thời gian bán",
      width: 170,
      render: (_, row) => formatDate(firstTicket(row) && firstTicket(row).datePrint),
    },
    {
      title: "Kỳ quay",
      width: 110,
      render: (_, row) => {
        const ticket = firstTicket(row);
        const termArray = ticket && ticket.termArray;
        if (!termArray) return "-";
        return Array.isArray(termArray) ? termArray[0] || "-" : termArray;
      },
    },
    {
      title: "Trúng thưởng",
      width: 120,
      render: (_, row) => winLabel(firstTicket(row)),
    },
    {
      title: "Chức năng",
      width: 90,
      fixed: "right",
      render: (_, row) => (
        <Button type="link" onClick={() => setSelected(row)}>
          Xem
        </Button>
      ),
    },
  ];

  const sumRevenue = sumAll.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);
  const sumFee = sumAll.reduce((sum, item) => sum + Number(item.servicePrice || 0), 0);
  const hasDateRange = Boolean(draft.datePrintFrom && draft.datePrintTo);

  return (
    <div style={{ padding: "0 16px 16px" }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={6} lg={4}>
          <div>Sản phẩm</div>
          <Select
            allowClear
            placeholder="Tất cả"
            style={{ width: "100%" }}
            options={PRODUCT_OPTIONS}
            value={draft.productId}
            onChange={(value) => setDraft((current) => ({ ...current, productId: value ?? null }))}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <div>Trạng thái vé</div>
          <Select
            allowClear
            placeholder="Tất cả"
            style={{ width: "100%" }}
            options={STATUS.map((item) => ({ value: item.value, label: item.label }))}
            value={draft.statusId}
            onChange={(value) => setDraft((current) => ({ ...current, statusId: value ?? null }))}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <div>Nhân viên</div>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Tất cả"
            style={{ width: "100%" }}
            options={staffList.map((staff) => ({ value: staff.id, label: staff.username }))}
            value={draft.staffId}
            onChange={(value) => setDraft((current) => ({ ...current, staffId: value ?? null }))}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <div>Id đơn</div>
          <Input
            value={draft.orderId}
            placeholder="Id đơn"
            onChange={(event) => setDraft((current) => ({ ...current, orderId: event.target.value }))}
            onPressEnter={onSearch}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <div>Id vé</div>
          <Input
            value={draft.ticketId}
            placeholder="Id vé"
            onChange={(event) => setDraft((current) => ({ ...current, ticketId: event.target.value }))}
            onPressEnter={onSearch}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <div>Số điện thoại</div>
          <Input
            value={draft.customerPhone}
            placeholder="Số điện thoại"
            onChange={(event) => setDraft((current) => ({ ...current, customerPhone: event.target.value }))}
            onPressEnter={onSearch}
          />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <div>
            Thời gian bán <span style={{ color: "#cf1322" }}>*</span>
          </div>
          <DatePicker.RangePicker
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            onChange={(dates) => {
              setDraft((current) => ({
                ...current,
                datePrintFrom: dates && dates[0] ? dates[0].format("YYYY-MM-DD") + " 00:00:00" : null,
                datePrintTo: dates && dates[1] ? dates[1].format("YYYY-MM-DD") + " 23:59:00" : null,
              }));
            }}
          />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <div>&nbsp;</div>
          <Space>
            <Button type="primary" loading={loading} disabled={!hasDateRange} onClick={onSearch}>
              Tìm kiếm
            </Button>
            <Button loading={exporting} disabled={!hasDateRange} onClick={onExport}>
              Xuất Excel
            </Button>
          </Space>
        </Col>
      </Row>

      <div style={{ margin: "12px 0" }}>
        <span>Tổng đơn: {total}</span>
        {sumAll.length > 0 ? (
          <span>
            {" "}
            - Tổng tiền: {money(sumRevenue)} - Phí dịch vụ: {money(sumFee)}
          </span>
        ) : null}
        {sumAll.length > 0 ? (
          <div>
            {sumAll.map((item) => (
              <span key={item.productId} style={{ marginRight: 16 }}>
                {getProductById(item.productId) || item.productId}: {item.countProductId} đơn / {money(item.totalPrice)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1900 }}
        pagination={{
          current: query.page,
          pageSize: query.size,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "50", "100", "200"],
          showTotal: (count) => `Tổng ${count} đơn`,
          onChange: (page, size) => {
            setQuery((current) => ({ ...current, page, size }));
          },
        }}
      />

      <Modal
        title={
          selected
            ? `Đơn #${selected.id} - ${getProductById(selected.productId) || ""}`
            : "Chi tiết đơn"
        }
        open={Boolean(selected)}
        footer={null}
        width={860}
        onCancel={() => setSelected(null)}
        destroyOnClose
      >
        {selected && Array.isArray(selected.tickets)
          ? selected.tickets.map((ticket) => (
              <div key={ticket.id} style={{ marginBottom: 16, borderBottom: "1px solid #f0f0f0", paddingBottom: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  Kiểu chơi: {getProductOptionById(ticket.productOptionId) || "-"}, Id vé: #{ticket.id}, Kỳ:{" "}
                  {Array.isArray(ticket.termArray) ? ticket.termArray.join(", ") : ticket.termArray || "-"}
                </div>
                {Array.isArray(ticket.value)
                  ? ticket.value.map((rowNumber, index) => (
                      <div key={index} style={{ display: "flex", marginBottom: 4 }}>
                        <span style={{ color: "#cf1322", width: 24 }}>{String.fromCharCode(65 + index)}</span>
                        <div>
                          {renderNumbers(ticket, rowNumber)}
                          {rowNumber.price != null ? <span> - {rowNumber.price}K</span> : null}
                        </div>
                      </div>
                    ))
                  : null}
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    {ticket.imageFront ? (
                      <img alt="Mặt trước" style={{ width: "100%" }} src={utils.getURLImage(ticket.imageFront)} />
                    ) : (
                      <div>Chưa có mặt trước</div>
                    )}
                    {isAdmin ? (
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file) => uploadTicketImage(ticket.id, "imageFront", file)}
                      >
                        <Button size="small" style={{ marginTop: 8 }} loading={uploadingKey === ticket.id + "-imageFront"}>
                          {ticket.imageFront ? "Đổi mặt trước" : "Tải mặt trước"}
                        </Button>
                      </Upload>
                    ) : null}
                  </Col>
                  <Col span={12}>
                    {ticket.imageBack ? (
                      <img alt="Mặt sau" style={{ width: "100%" }} src={utils.getURLImage(ticket.imageBack)} />
                    ) : (
                      <div>Chưa có mặt sau</div>
                    )}
                    {isAdmin ? (
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file) => uploadTicketImage(ticket.id, "imageBack", file)}
                      >
                        <Button size="small" style={{ marginTop: 8 }} loading={uploadingKey === ticket.id + "-imageBack"}>
                          {ticket.imageBack ? "Đổi mặt sau" : "Tải mặt sau"}
                        </Button>
                      </Upload>
                    ) : null}
                  </Col>
                </Row>
              </div>
            ))
          : null}
      </Modal>
    </div>
  );
}

export default OrderList;
